/**
 * 🧊 An item's picture, drawn as the block itself.
 *
 * 🎯 **haugur, 2026-08-18:** *"we need to build any graphics, that's how we do
 * it"* — the Replicate workflow in `Development/09_Texture_Generation.md`. This
 * uses that work rather than repeating it: **the icon is a cube faced with the
 * tile the game actually draws**, which is the generated 1024 px picture where
 * one exists and the procedural recipe everywhere else.
 *
 * 🔴 **So nothing new is generated and nothing is spent.** The alternative was
 * 29 fresh prompts, and §4c of that document is why it would have been the wrong
 * road: **image models return opaque pictures and no prompt produces an alpha
 * channel**, so every one would have needed its background keyed out — and a
 * 1024 px picture shown at 27 px loses the crispness that made it worth
 * generating.
 *
 * ⚠️ **It also cannot drift.** An icon is rendered from the block's own tile, so
 * regenerating a texture improves its item picture for free, and no icon can
 * quietly stop looking like the thing it stands for.
 *
 * ⚠️ **`hud-parts.ts` must never import this.** The four design generators in
 * `tools/` are plain `.mjs` that resolve `hud-parts.ts` directly; a workspace
 * import from there would break every sample sheet. The drawing module takes a
 * finished data URI and knows nothing about where it came from.
 */

import { DEFAULT_TILE, blockIdForKey, renderTile, tilePlans, type FaceKind } from '@lofi/shared';

/** How big the rendered cube is, in px. Drawn at 27 and scaled by the browser. */
const ICON = 64;

/**
 * How much darker each face is than the top.
 *
 * ⚠️ **Shading the FACES, not the texture.** A cube reads as one object catching
 * light because its three faces differ in value while sharing a material — the
 * same rule the HUD's rods follow. Minecraft's own item cubes do exactly this.
 */
const FACE_SHADE: Record<'top' | 'left' | 'right', number> = {
  top: 0,
  left: 0.22,
  right: 0.42,
};

const cache = new Map<string, string | undefined>();

/** The tile the game draws for one face of one block, as RGBA. */
function tileFor(blockKey: string, face: FaceKind): Uint8Array | undefined {
  const id = blockIdForKey(blockKey);
  if (id === undefined) return undefined;
  const plan = tilePlans().find((p) => p.block === id && p.face === face);
  /* ⚠️ `DEFAULT_TILE` on purpose: `renderTile` only reaches for the authored
     Replicate picture at that size. Asking for anything else silently gets the
     procedural recipe instead, which is the fallback and not the good one. */
  return plan === undefined ? undefined : renderTile(plan, DEFAULT_TILE);
}

/** Put RGBA pixels on their own canvas so they can be used as a draw source. */
function asCanvas(rgba: Uint8Array, size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  if (g) {
    const img = g.createImageData(size, size);
    img.data.set(rgba);
    g.putImageData(img, 0, 0);
  }
  return c;
}

/**
 * Draw one face of the cube: a square tile sheared into a parallelogram.
 *
 * `m` maps the unit square onto the face. Clipping to the same shape is what
 * stops a sheared square spilling over the two faces beside it.
 */
function face(
  g: CanvasRenderingContext2D,
  tile: HTMLCanvasElement,
  points: readonly (readonly [number, number])[],
  m: readonly [number, number, number, number, number, number],
  shade: number,
): void {
  g.save();
  g.beginPath();
  const [first, ...rest] = points;
  if (!first) return;
  g.moveTo(first[0], first[1]);
  for (const [px, py] of rest) g.lineTo(px, py);
  g.closePath();
  g.clip();
  g.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  g.imageSmoothingEnabled = false;
  g.drawImage(tile, 0, 0, 1, 1);
  g.setTransform(1, 0, 0, 1, 0, 0);
  if (shade > 0) {
    g.fillStyle = `rgba(0,0,0,${String(shade)})`;
    g.fill();
  }
  g.restore();
}

/**
 * A data URI of this block drawn as an isometric cube, or `undefined` if the
 * block has no tile at all.
 *
 * Cached: ten slots redrawing on every pickup would otherwise re-render the
 * same cube ten times a second.
 */
export function blockIconUri(blockKey: string): string | undefined {
  const hit = cache.get(blockKey);
  if (hit !== undefined || cache.has(blockKey)) return hit;

  const top = tileFor(blockKey, 'top');
  const side = tileFor(blockKey, 'side');
  let uri: string | undefined;

  if (top && side) {
    const c = document.createElement('canvas');
    c.width = ICON;
    c.height = ICON;
    const g = c.getContext('2d');
    if (g) {
      const topTile = asCanvas(top, DEFAULT_TILE);
      const sideTile = asCanvas(side, DEFAULT_TILE);
      /* A 2:1 isometric cube on a 64 px square: 8 px of margin, a 24 px
         half-width and a 14 px face rise. Corners, clockwise from the apex. */
      const w = 24;
      const h = 14;
      const cx = ICON / 2;
      const ty = 6;
      const apex: [number, number] = [cx, ty];
      const rightT: [number, number] = [cx + w, ty + h];
      const bottomT: [number, number] = [cx, ty + h * 2];
      const leftT: [number, number] = [cx - w, ty + h];
      const drop = 22;

      face(g, topTile, [apex, rightT, bottomT, leftT], [w, h, -w, h, cx, ty], FACE_SHADE.top);
      face(
        g,
        sideTile,
        [leftT, bottomT, [cx, bottomT[1] + drop], [cx - w, leftT[1] + drop]],
        [w, h, 0, drop, cx - w, leftT[1]],
        FACE_SHADE.left,
      );
      face(
        g,
        sideTile,
        [bottomT, rightT, [cx + w, rightT[1] + drop], [cx, bottomT[1] + drop]],
        [w, -h, 0, drop, cx, bottomT[1]],
        FACE_SHADE.right,
      );
      uri = c.toDataURL('image/png');
    }
  }

  cache.set(blockKey, uri);
  return uri;
}
